import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import HelpIcon from './HelpIcon';
import { getHelpContent } from '../utils/helpContent.js';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/theme';
import ExportPreviewModal from './ExportPreviewModal';
import { API_ENDPOINTS } from '../config.js';

export default function ExportModal({ open, onClose, data, onConfirm, targetFields, companyData, documentData }) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  // mapping: { TaurusField: inputColIndex }
  const [mapping, setMapping] = useState({});
  const [colOffset, setColOffset] = useState(0); // for horizontal scroll
  const previewCols = 6; // number of columns to show at once
  const [exportFormat] = useState('taurus'); // Default to Taurus format
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewText, setPreviewText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Draggable modal state
  const [dragging, setDragging] = useState(false);
  const [modalPos, setModalPos] = useState({ x: 0, y: 0 });
  const dragStart = useRef({ x: 0, y: 0 });
  const mouseStart = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (data && data[0]) {
      // Reset mapping and scroll when data changes
      setMapping({});
      setColOffset(0);
    }
  }, [data]);

  useEffect(() => {
    if (!dragging) return;
    const handleMouseMove = (e) => {
      setModalPos({
        x: dragStart.current.x + (e.clientX - mouseStart.current.x),
        y: dragStart.current.y + (e.clientY - mouseStart.current.y),
      });
    };
    const handleMouseUp = () => setDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);

  if (!open) return null;
  if (!data || !data.length) return null;

  const handleChange = (field, colIdx) => {
    setMapping(m => ({ ...m, [field]: colIdx === '' ? undefined : Number(colIdx) }));
  };

  const handleConfirm = () => {
    // Validate that at least one field is mapped
    const mappedFields = Object.values(mapping).filter(v => v !== undefined && v !== null && v !== '');
    if (mappedFields.length === 0) {
      alert(t('exportModal.noMappingError') || 'Please map at least one field before exporting.');
      return;
    }
    
    onConfirm && onConfirm(mapping, exportFormat);
    onClose && onClose();
  };

  const handlePreviewExport = async () => {
    // Validate that at least one field is mapped
    const mappedFields = Object.values(mapping).filter(v => v !== undefined && v !== null && v !== '');
    if (mappedFields.length === 0) {
      alert(t('exportModal.noMappingError') || 'Please map at least one field before previewing.');
      return;
    }
    
    setPreviewLoading(true);
    try {
      // Map company data to Taurus SECTION1_FIELDS format
      const taurusCompanyData = companyData ? {
        'NAZIV': companyData.name || '',
        'SIFRA DELATNOSTI': '',
        'POSTA': companyData.postalCode || '',
        'MESTO': companyData.city || '',
        'ADRESA': companyData.address || '',
        'FAX': '',
        'MAIL': companyData.email || '',
        'WEB': companyData.website || '',
        'RACUN': '',
        'INTERNA SIFRA PARTNERA': ''
      } : {};
      
      const requestBody = { 
        mappedData: data, 
        mapping,
        companyData: taurusCompanyData,
        documentData: documentData || {}
      };
      
      const res = await fetch(API_ENDPOINTS.TAURUS_EXPORT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      
      if (!res.ok) {
        const errorText = await res.text().catch(() => '');
        alert(`Preview failed: ${errorText || 'Unknown error'}`);
        setPreviewLoading(false);
        return;
      }
      
      const text = await res.text();
      setPreviewText(text);
      setPreviewModalOpen(true);
    } catch (err) {
      console.error('Preview error:', err);
      alert(`Preview failed: ${err.message || 'Unknown error'}`);
    }
    setPreviewLoading(false);
  };

  // Data preview controls
  const maxColOffset = Math.max(0, data[0].length - previewCols);
  const visibleCols = data[0].slice(colOffset, colOffset + previewCols);

  // Drag handlers
  const onHeaderMouseDown = (e) => {
    setDragging(true);
    dragStart.current = { ...modalPos };
    mouseStart.current = { x: e.clientX, y: e.clientY };
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(30,41,59,0.18)',
      zIndex: 10001,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      userSelect: dragging ? 'none' : undefined,
    }}>
      <div
        style={{
          background: colors.card,
          borderRadius: 12,
          boxShadow: isDark ? '0 4px 32px rgba(0,0,0,0.5)' : '0 4px 32px rgba(30,41,59,0.18)',
          padding: 32,
          minWidth: 600,
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'absolute',
          left: `calc(50% + ${modalPos.x}px)`,
          top: `calc(50% + ${modalPos.y}px)`,
          transform: 'translate(-50%, -50%)',
          cursor: dragging ? 'grabbing' : 'default',
        }}
      >
        <div
          style={{
            marginTop: 0,
            marginBottom: 18,
            color: colors.primary,
            fontSize: 24,
            fontWeight: 700,
            cursor: 'grab',
            userSelect: 'none',
            borderBottom: `2px solid ${colors.border}`,
            paddingBottom: 8,
            marginLeft: -32,
            marginRight: -32,
            paddingLeft: 32,
            paddingRight: 32,
            background: colors.card,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
          onMouseDown={onHeaderMouseDown}
        >
          <span>{t('exportModal.title')}</span>
          <HelpIcon
            content={getHelpContent('exportModal.fieldMapping')?.short}
            detailedContent={getHelpContent('exportModal.fieldMapping')?.detailed}
            position="bottom"
            size={18}
          />
        </div>
        <div style={{overflowX: 'auto'}}>
          <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 18 }}>
            <thead>
              <tr>
                <th style={{ padding: 6, border: `1px solid ${colors.border}`, background: colors.backgroundHover, color: colors.text }}>{t('exportModal.targetField') || 'Target Field'}</th>
                <th style={{ padding: 6, border: `1px solid ${colors.border}`, background: colors.backgroundHover, color: colors.text }}>{t('exportModal.inputColumn') || 'Input Column'}</th>
              </tr>
            </thead>
            <tbody>
              {targetFields.map((field, i) => (
                <tr key={field}>
                  <td style={{ border: `1px solid ${colors.border}`, padding: 6, fontSize: 15, color: colors.text }}>{field}</td>
                  <td style={{ border: `1px solid ${colors.border}`, padding: 6 }}>
                    <select
                      value={mapping[field] !== undefined ? mapping[field] : ''}
                      onChange={e => handleChange(field, e.target.value)}
                      style={{ padding: '4px 8px', borderRadius: 4, border: `1px solid ${colors.border}`, fontSize: 14, background: colors.background, color: colors.text }}
                    >
                      <option value="">{t('exportModal.unmapped')}</option>
                      {data[0].map((_, colIdx) => (
                        <option key={colIdx} value={colIdx}>{t('exportModal.column')} {colIdx + 1}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Data Preview with horizontal scroll */}
        <div style={{
          borderTop: `3px solid ${colors.primary}`,
          margin: '18px 0 24px 0',
          paddingTop: 12,
          background: colors.backgroundLight,
          borderRadius: 8,
          position: 'relative',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 600, color: colors.primary, fontSize: 15 }}>{t('exportModal.dataPreview')}</span>
            <div style={{ flex: 1 }} />
            <button
              onClick={() => setColOffset(o => Math.max(0, o - 1))}
              disabled={colOffset === 0}
              style={{ marginRight: 6, padding: '2px 10px', borderRadius: 4, border: `1px solid ${colors.border}`, background: colOffset === 0 ? colors.backgroundHover : colors.background, color: colors.primary, cursor: colOffset === 0 ? 'not-allowed' : 'pointer', fontWeight: 700 }}
            >&lt;</button>
            <button
              onClick={() => setColOffset(o => Math.min(maxColOffset, o + 1))}
              disabled={colOffset === maxColOffset}
              style={{ padding: '2px 10px', borderRadius: 4, border: `1px solid ${colors.border}`, background: colOffset === maxColOffset ? colors.backgroundHover : colors.background, color: colors.primary, cursor: colOffset === maxColOffset ? 'not-allowed' : 'pointer', fontWeight: 700 }}
            >&gt;</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  {visibleCols.map((_, colIdx) => (
                    <th key={colIdx + colOffset} style={{ padding: 6, border: `1px solid ${colors.border}`, background: colors.backgroundHover, color: colors.text, minWidth: 80 }}>
                      {t('exportModal.column')} {colIdx + colOffset + 1}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.slice(0, 6).map((row, i) => (
                  <tr key={i}>
                    {row.slice(colOffset, colOffset + previewCols).map((cell, j) => (
                      <td key={j + colOffset} style={{ border: `1px solid ${colors.border}`, padding: 6, fontSize: 15, color: colors.text }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 6, border: `1px solid ${colors.border}`, background: colors.backgroundHover, color: colors.text, fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>{t('exportModal.cancel')}</button>
          <button 
            onClick={handlePreviewExport} 
            disabled={previewLoading}
            style={{ 
              padding: '8px 18px', 
              borderRadius: 6, 
              border: `1px solid ${colors.border}`, 
              background: previewLoading ? colors.backgroundHover : colors.background, 
              color: colors.text, 
              fontWeight: 600, 
              fontSize: 15, 
              cursor: previewLoading ? 'not-allowed' : 'pointer',
              opacity: previewLoading ? 0.6 : 1
            }}
          >
            {previewLoading ? (t('exportPreviewModal.loading') || 'Generating preview...') : (t('exportModal.previewExport') || 'Preview Export')}
          </button>
          <button onClick={handleConfirm} style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: colors.primary, color: 'white', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>{t('exportModal.confirmExport')}</button>
        </div>
      </div>
      <ExportPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        previewText={previewText}
      />
    </div>
  );
} 